import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Clock, 
  Users, 
  FolderPlus, 
  Timer,
  BarChart3,
  UserPlus,
  Calendar,
  PlusCircle
} from 'lucide-react';

const OnboardingModal = ({ isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Données des slides avec texte descriptif
  const slides = [
    {
      id: 1,
      title: "Suivi des temps et projets",
      description: "Comparez facilement le temps passé sur chaque projet par rapport au temps planifié.",
      content: "Visualisez en temps réel l'avancement de vos projets grâce à nos tableaux de bord intuitifs. Suivez les budgets temps, identifiez les dépassements et optimisez la planification de vos équipes.",
      icon: <BarChart3 className="w-16 h-16 text-blue-600" />,
      image: "/dashboard.png",
      features: [
        "Comparaison temps planifié vs consommé",
        "Alertes de dépassement de budget",
        "Graphiques de progression en temps réel",
        "Reporting détaillé par phase de projet"
      ],
      color: "bg-blue-50 border-blue-200"
    },
    {
      id: 2,
      title: "Gestion d'équipe simplifiée",
      description: "Ajoutez facilement des personnes à vos équipes projet et gérez leurs affectations.",
      content: "Constituez vos équipes efficacement en affectant les bonnes personnes aux bons projets. Gérez les rôles, capacités et disponibilités de chaque membre.",
      icon: <UserPlus className="w-16 h-16 text-green-600" />,
      image: "/users.png",
      features: [
        "Ajout rapide de membres d'équipe",
        "Gestion des rôles et responsabilités",
        "Calcul automatique des capacités",
        "Vue d'ensemble des affectations"
      ],
      color: "bg-green-50 border-green-200"
    },
    {
      id: 3,
      title: "Création de projets intuitive",
      description: "Créez et organisez vos projets avec des phases, budgets et échéances clairement définis.",
      content: "Structurez vos projets en phases distinctes avec des budgets temps dédiés. Définissez les dates de début et fin pour un suivi optimal des délais.",
      icon: <FolderPlus className="w-16 h-16 text-purple-600" />,
      image: "/projets.png",
      features: [
        "Création rapide de nouveaux projets",
        "Organisation en phases budgétées",
        "Planification des échéances",
        "Classification par catégories"
      ],
      color: "bg-purple-50 border-purple-200"
    },
    {
      id: 4,
      title: "Saisie de temps flexible",
      description: "Saisissez vos temps depuis différents points d'entrée : projet, personne ou vue globale.",
      content: "Adaptez la saisie à votre façon de travailler. Que vous préfériez saisir par projet, par personne ou avoir une vue d'ensemble, tous les chemins mènent à un suivi précis.",
      icon: <Timer className="w-16 h-16 text-orange-600" />,
      image: "/temps.png",
      features: [
        "Saisie depuis la vue projet",
        "Saisie depuis la fiche personne",
        "Vue consolidée des temps saisis",
        "Distinction temps facturable/non facturable"
      ],
      color: "bg-orange-50 border-orange-200"
    }
  ];

  // Gestion de la navigation des slides
  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Fermeture avec Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Auto-progression des slides (optionnel)
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 8000); // 8 secondes par slide

    return () => clearInterval(interval);
  }, [isOpen, currentSlide]);

  if (!isOpen) return null;

  const currentSlideData = slides[currentSlide];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Bienvenue dans Time Tracker</h2>
              <p className="text-sm text-gray-600">Découvrez les fonctionnalités principales</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className={`${currentSlideData.color} rounded-2xl p-8 border-2 transition-all duration-300 ${isAnimating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Texte */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    {currentSlideData.icon}
                    <span className="text-lg font-semibold text-gray-600">
                      {String(currentSlide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {currentSlideData.title}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {currentSlideData.description}
                  </p>
                  <p className="text-gray-600">
                    {currentSlideData.content}
                  </p>
                </div>

                {/* Fonctionnalités */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800">Fonctionnalités clés :</h4>
                  <ul className="space-y-2">
                    {currentSlideData.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-current rounded-full mt-2 flex-shrink-0 opacity-60"></div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Image de démonstration */}
              <div className="flex items-center justify-center">
                <div className="w-full h-64 lg:h-80 bg-white bg-opacity-50 rounded-xl border-2 border-gray-200 overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group relative">
                  <img
                    src={currentSlideData.image}
                    alt={`Illustration pour ${currentSlideData.title}`}
                    className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      // Fallback en cas d'erreur de chargement
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback si l'image ne charge pas */}
                  <div className="absolute inset-0 w-full h-full bg-white bg-opacity-50 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500" style={{ display: 'none' }}>
                    <div className="text-4xl mb-2">🖼️</div>
                    <p className="text-sm font-medium">Image en cours de chargement</p>
                    <p className="text-xs">Slide {currentSlide + 1}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          {/* Bouton Précédent */}
          <button
            onClick={prevSlide}
            disabled={isAnimating}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Précédent</span>
          </button>

          {/* Indicateurs de slides */}
          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                disabled={isAnimating}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentSlide
                    ? 'bg-blue-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Aller au slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Bouton Suivant / Terminer */}
          {currentSlide === slides.length - 1 ? (
            <button
              onClick={onClose}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium"
            >
              <span>Commencer</span>
              <PlusCircle className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={nextSlide}
              disabled={isAnimating}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <span>Suivant</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Barre de progression */}
        <div className="h-1 bg-gray-200">
          <div 
            className="h-full bg-blue-600 transition-all duration-300 ease-out"
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
